import * as THREE from 'three';
import {
  captureSubtreeMaterialOpacity,
  restoreSubtreeMaterialOpacity,
} from './materialOpacitySnapshot';
import { refreshAnimationTransformAnchors } from './syncObjectTransform';

/**
 * Utility to save and restore object state (transform + material opacity) in the scene.
 * Used when switching between Preview and Design so mid-animation values (e.g. fade at 0.5) do not stick.
 */
class SceneStateHelper {
  constructor() {
    this.initialStates = new Map();
  }

  /**
   * Saves the current transform of all relevant objects in the scene.
   * @param {THREE.Scene} scene - The Three.js scene.
   */
  saveStates(scene) {
    if (!scene) return;

    // Clear existing states if we are starting a new save session
    // Usually called just before entering Preview mode
    this.initialStates.clear();

    scene.traverse((obj) => {
      // Skip system objects or objects without a name/ID
      if (!obj.name && !obj.uuid) return;
      
      // Skip specific system objects if they haven't been removed yet
      if (['gridHelper', 'transformControls', 'directionalLightHelper', 'cameraHelper'].includes(obj.name)) return;
      
      // Skip the scene itself
      if (obj.type === 'Scene') return;

      const materialOpacitySnapshot = captureSubtreeMaterialOpacity(obj);
      const quaternion =
        obj.userData?.isHotspot && obj.userData?.baseQuaternion
          ? obj.userData.baseQuaternion.clone()
          : obj.quaternion.clone();
      const entry = {
        position: obj.position.clone(),
        quaternion,
        scale: obj.scale.clone(),
        visible: obj.visible,
      };
      if (Object.keys(materialOpacitySnapshot).length > 0) {
        entry.materialOpacitySnapshot = materialOpacitySnapshot;
      }
      this.initialStates.set(obj.uuid, entry);
    });
  }

  /**
   * Restores the saved transform of all objects in the scene.
   * @param {THREE.Scene} scene - The Three.js scene.
   */
  restoreStates(scene) {
    if (!scene || this.initialStates.size === 0) return;

    scene.traverse((obj) => {
      const state = this.initialStates.get(obj.uuid);
      if (state) {
        obj.position.copy(state.position);
        obj.quaternion.copy(state.quaternion);
        obj.scale.copy(state.scale);
        obj.visible = state.visible;

        // Keep Euler in sync for UI (cameras/hotspots read .rotation, not only quaternion).
        obj.rotation.setFromQuaternion(state.quaternion, obj.rotation.order);

        if (obj.userData?.isHotspot) {
          if (!obj.userData.baseQuaternion) {
            obj.userData.baseQuaternion = state.quaternion.clone();
          } else {
            obj.userData.baseQuaternion.copy(state.quaternion);
          }
        }

        if (state.materialOpacitySnapshot) {
          restoreSubtreeMaterialOpacity(obj, state.materialOpacitySnapshot);
        }

        refreshAnimationTransformAnchors(obj);

        // Force matrix update
        obj.updateMatrix();
        obj.updateMatrixWorld(true);
      }
    });
  }

  /**
   * Clears all stored states.
   */
  clear() {
    this.initialStates.clear();
  }

  hasSavedStates() {
    return this.initialStates.size > 0;
  }

  /**
   * Refresh saved state for one object (after user edits transform/opacity in design).
   */
  updateObjectState(obj) {
    if (!obj?.uuid || this.initialStates.size === 0) return;

    const materialOpacitySnapshot = captureSubtreeMaterialOpacity(obj);
    const quaternion =
      obj.userData?.isHotspot && obj.userData?.baseQuaternion
        ? obj.userData.baseQuaternion.clone()
        : obj.quaternion.clone();
    const entry = {
      position: obj.position.clone(),
      quaternion,
      scale: obj.scale.clone(),
      visible: obj.visible,
    };
    if (Object.keys(materialOpacitySnapshot).length > 0) {
      entry.materialOpacitySnapshot = materialOpacitySnapshot;
    }
    this.initialStates.set(obj.uuid, entry);
  }
}

export const sceneStateHelper = new SceneStateHelper();
